import { useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { BaseListView } from "@/base";
import { StatStrip } from "@/base/parts";
import { CollapsibleBox } from "@/components/CollapsibleBox";
import type { BondSubmission } from "@/lib/brokerBonds";
import type { InvoiceRow } from "@/lib/invoices";
import {
  submissionBillingTiles,
  submissionBillingTotals,
} from "@/lib/submissionBilling";
import { submissionBillingListView } from "@/pages/submissionBillingListView";

// BRK-S426, BRK-S502 — the submission page's Billing tab: the four tiles,
// then the order's invoices in a collapsible "Invoices" box — the same box the
// Submission Profile stacks (`SubmissionInvoicesBox`). The tiles are the
// wireframe's Premium billed · Paid · Balance due · Next payment due, from
// what THIS order's served invoice rows add up to (`submissionBillingTiles`);
// until the rows are read, or when one cannot be read, a figure is "—".
// Read-only: a PDF download per row and no Pay now, payment type, method or
// reference (D5, BRK-S187).

type BillingOrder = Pick<
  BondSubmission,
  "id" | "accountId" | "accountLegalName"
>;

/** The invoices list for this order, and the rows it read (for the tiles). */
function useOrderInvoices(order: BillingOrder) {
  const { authedRequest, authedBlob } = useAuth();
  const [rows, setRows] = useState<InvoiceRow[] | null>(null);
  const def = useMemo(
    () =>
      submissionBillingListView({
        request: authedRequest,
        fetchBlob: authedBlob,
        accountId: order.accountId,
        orderId: order.id,
        onRows: setRows,
      }),
    [authedRequest, authedBlob, order.accountId, order.id],
  );
  return { rows, def };
}

/** The "Invoices" box (the wireframe's box e), on the Profile and the
 *  Billing tab alike. */
export function SubmissionInvoicesBox({ order }: { order: BillingOrder }) {
  const { def } = useOrderInvoices(order);
  return <InvoicesBox order={order} def={def} />;
}

function InvoicesBox({
  order,
  def,
}: {
  order: BillingOrder;
  def: ReturnType<typeof submissionBillingListView>;
}) {
  return (
    <CollapsibleBox
      title="Invoices"
      actions={
        <p className="text-sm text-brk-dim">
          Premium is billed per bond to{" "}
          {order.accountLegalName ?? "the insured"}
        </p>
      }
    >
      <BaseListView def={def} embedded />
    </CollapsibleBox>
  );
}

export function SubmissionBillingPanel({ order }: { order: BillingOrder }) {
  const { rows, def } = useOrderInvoices(order);
  const totals = submissionBillingTotals(rows);
  const note =
    totals.state === "unreadable"
      ? "An invoice’s status or amount couldn’t be read, so the totals aren’t shown."
      : undefined;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <StatStrip
        label="Billing summary"
        stats={submissionBillingTiles(rows).map((t) => ({
          label: t.label,
          value: (
            <>
              <span className="block">{t.value}</span>
              <span className="block text-xs font-normal text-brk-dim">
                {t.caption}
              </span>
            </>
          ),
        }))}
        note={note}
      />
      <InvoicesBox order={order} def={def} />
    </div>
  );
}
